import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  type SystemUser,
} from '../api';
import { useAuth } from '../contexts/AuthContext';

export default function AdminUsers() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [secret, setSecret] = useState('');
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<SystemUser | null>(null);
  const [formType, setFormType] = useState<'admin' | 'partner'>('admin');
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formRole, setFormRole] = useState<'owner' | 'partner'>('partner');
  const [formCommissionRate, setFormCommissionRate] = useState<number>(0.03);
  const [formPassword, setFormPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const adminSecret = (import.meta.env.VITE_ADMIN_SECRET as string) || secret;

  const load = (s?: string) => {
    const s2 = s ?? adminSecret;
    if (!s2?.trim()) {
      setUnauthorized(true);
      return;
    }
    setError(null);
    setUnauthorized(false);
    setLoading(true);
    getAdminUsers(s2.trim())
      .then((r) => setUsers(r.users))
      .catch((e) => {
        const msg = e?.message ?? '';
        setUsers([]);
        if (msg.includes('401') || msg.includes('Unauthorized')) setUnauthorized(true);
        else setError(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const envSecret = (import.meta.env.VITE_ADMIN_SECRET as string) || '';
    if (envSecret.trim()) {
      load(envSecret);
    } else {
      setUnauthorized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = (type: 'admin' | 'partner') => {
    setFormType(type);
    setFormEmail('');
    setFormName('');
    setFormRole('partner');
    setFormCommissionRate(0.03);
    setFormPassword('');
    setModal('create');
  };

  const openEdit = (u: SystemUser) => {
    setEditing(u);
    setFormType(u.type);
    setFormEmail(u.email);
    setFormName(u.type === 'partner' ? u.name : '');
    setFormRole((u.role === 'owner' ? 'owner' : 'partner') as 'owner' | 'partner');
    setFormCommissionRate(u.type === 'partner' ? (u.commission_rate ?? 0.03) : 0.03);
    setFormPassword('');
    setModal('edit');
  };

  const saveCreate = async () => {
    if (!adminSecret?.trim()) {
      setUnauthorized(true);
      setError('Enter the admin secret to create users.');
      return;
    }
    setSaving(true);
    try {
      await createAdminUser(adminSecret, {
        type: formType,
        email: formEmail.trim(),
        password: formPassword.trim() || undefined,
        name: formType === 'partner' ? formName.trim() || formEmail : undefined,
        role: formRole,
        ...(formType === 'partner' && { commission_rate: formCommissionRate }),
      });
      setModal(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editing || !adminSecret?.trim()) {
      if (!adminSecret?.trim()) {
        setUnauthorized(true);
        setError('Enter the admin secret to update users.');
      }
      return;
    }
    setSaving(true);
    try {
      await updateAdminUser(adminSecret, editing.type, editing.id, {
        email: formEmail.trim(),
        ...(editing.type === 'partner' && { name: formName.trim() }),
        role: formRole,
        ...(editing.type === 'partner' && { commission_rate: formCommissionRate }),
        ...(formPassword.trim() && { password: formPassword }),
      });
      setModal(null);
      setEditing(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const addRights = async (u: SystemUser) => {
    if (!adminSecret?.trim()) {
      setUnauthorized(true);
      setError('Enter the admin secret to update users.');
      return;
    }
    try {
      await updateAdminUser(adminSecret, u.type, u.id, { role: 'owner' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const handleDelete = async (u: SystemUser) => {
    if (!adminSecret?.trim()) {
      setUnauthorized(true);
      setError('Enter the admin secret to delete users.');
      return;
    }
    if (!confirm(`Delete ${u.type} ${u.email}?`)) return;
    try {
      await deleteAdminUser(adminSecret, u.type, u.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-charcoal">
      <header className="bg-charcoal text-gold border-b-2 border-gold/40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <Link to="/dashboard" className="text-xl font-semibold text-gold tracking-tight hover:text-gold/80 transition-colors shrink-0">
            The Station
          </Link>
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <span className="text-sm text-gold/90 font-medium shrink-0">Admin · User Management</span>
            <button type="button" onClick={() => { logout(); navigate('/login', { replace: true }); }} className="text-sm text-gold/80 hover:text-gold transition-colors">
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-semibold text-gold">User Management</h1>
          <Link to="/dashboard" className="text-sm font-medium text-gold hover:text-gold/80 transition-colors shrink-0">
            ← Back to Portal
          </Link>
        </div>
        {unauthorized && (
          <form onSubmit={(e) => { e.preventDefault(); load(secret.trim()); }} className="mb-6 rounded-xl border border-gold/50 bg-gold/10 p-4 max-w-md">
            <label className="block text-sm font-medium text-gold mb-2">Admin secret</label>
            <div className="flex gap-2">
              <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} className="flex-1 rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white" placeholder="Enter secret" />
              <button type="submit" className="rounded-lg bg-gold text-charcoal font-medium px-4 py-2 hover:bg-gold/90">Unlock</button>
            </div>
          </form>
        )}
        {error && <div className="mb-4 p-4 rounded-lg bg-red-500/20 border border-red-400/50 text-red-300 text-sm">{error}</div>}
        <div className="flex flex-wrap gap-2 mb-4">
          <button type="button" onClick={() => openCreate('admin')} className="rounded-lg bg-gold text-charcoal font-medium px-4 py-2 hover:bg-gold/90">
            Create Admin
          </button>
          <button type="button" onClick={() => openCreate('partner')} className="rounded-lg border border-gold text-gold font-medium px-4 py-2 hover:bg-gold/10">
            Create Partner
          </button>
        </div>
        {loading && <div className="text-gold/80">Loading users…</div>}
        {!loading && users.length > 0 && (
          <div className="rounded-xl border border-leather bg-leather/40 overflow-x-auto">
            <table className="w-full text-left min-w-[800px]">
              <thead className="bg-leather text-gold/90 text-sm">
                <tr>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Commission</th>
                  <th className="px-4 py-3 font-medium w-48">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-leather/50">
                {users.map((u) => (
                  <tr key={`${u.type}-${u.id}`} className="hover:bg-leather/20">
                    <td className="px-4 py-3 text-station-muted">{u.type}</td>
                    <td className="px-4 py-3 font-medium text-white">{u.type === 'partner' ? u.name : '—'}</td>
                    <td className="px-4 py-3 text-station-muted">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={u.role === 'owner' ? 'text-gold font-medium' : 'text-station-muted'}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3 text-station-muted">{u.type === 'partner' ? `${((u.commission_rate ?? 0.03) * 100).toFixed(1)}%` : '—'}</td>
                    <td className="px-4 py-3 flex flex-wrap gap-2">
                      <button type="button" onClick={() => openEdit(u)} className="text-gold hover:underline text-sm">Edit</button>
                      {u.role !== 'owner' && (
                        <button type="button" onClick={() => addRights(u)} className="text-gold hover:underline text-sm">Add Rights</button>
                      )}
                      <button type="button" onClick={() => handleDelete(u)} className="text-red-400 hover:underline text-sm">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && users.length === 0 && !unauthorized && <div className="rounded-xl border border-leather bg-leather/40 p-8 text-center text-station-muted">No users. Create an admin or partner above.</div>}
      </main>

      {(modal === 'create' || modal === 'edit') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-y-auto" onClick={() => setModal(null)}>
          <div className="rounded-xl border border-leather bg-charcoal p-6 w-full max-w-md min-w-0 my-auto shadow-xl flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gold">{modal === 'create' ? `Create ${formType}` : 'Edit user'}</h3>
            {modal === 'create' && (
              <div>
                <label className="block text-sm font-medium text-gold/90 mb-1">Type</label>
                <select value={formType} onChange={(e) => setFormType(e.target.value as 'admin' | 'partner')} className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white">
                  <option value="admin">Admin</option>
                  <option value="partner">Partner</option>
                </select>
              </div>
            )}
            {formType === 'partner' && (
              <div>
                <label className="block text-sm font-medium text-gold/90 mb-1">Name</label>
                <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white" placeholder="Partner name" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gold/90 mb-1">Email</label>
              <input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white" placeholder="email@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gold/90 mb-1">Role</label>
              <select value={formRole} onChange={(e) => setFormRole(e.target.value as 'owner' | 'partner')} className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white">
                <option value="partner">Partner</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            {formType === 'partner' && (
              <div>
                <label className="block text-sm font-medium text-gold/90 mb-1">Commission rate (0–1, e.g. 0.03 = 3%)</label>
                <input type="number" min={0} max={1} step={0.01} value={formCommissionRate} onChange={(e) => setFormCommissionRate(Number(e.target.value))} className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gold/90 mb-1">{modal === 'create' ? 'Password (optional)' : 'New password (leave blank to keep)'}</label>
              <input type="password" value={formPassword} onChange={(e) => setFormPassword(e.target.value)} className="w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white" placeholder="••••••••" />
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded-lg border border-leather text-station-muted hover:text-white">Cancel</button>
              <button type="button" onClick={modal === 'create' ? saveCreate : saveEdit} disabled={saving} className="px-4 py-2 rounded-lg bg-gold text-charcoal font-medium hover:bg-gold/90 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
